<template>
    <layout :studio="studio" :cart="cart" :selected-category="selectedCategory" :selected-group="selectedGroup" :search="search">
        <template #title>
            <h3 v-if="selectedCategory" class="mt-3 mb-4 text-lg lg:mt-0 xl:text-3xl">
                {{ selectedCategory.name }}<small v-if="selectedGroup"> - {{ selectedGroup.name }}</small>
            </h3>
            <h3 v-else class="mt-3 mb-4 text-lg lg:mt-0 xl:text-3xl">Search Results</h3>
        </template>
        <h4 v-if="search">You searched for: {{ search }}</h4>
        <div v-loading="tableIsLoading" class="bg-white p-4 shadow-lg">
            <h4 v-if="loaded && data.length === 0" class="my-8 text-center">No products available.</h4>
            <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                <div v-for="product in data" :key="`product-${product.id}`" class="mb-3 flex flex-col border-b border-gray-200 pb-3">
                    <div class="header text-center font-bold">
                        <a :href="route('studio.shop.item', { studioSlug: studio.slug, product: product.id })" title="Details">
                            {{ product.name }}
                        </a>
                    </div>
                    <div class="image">
                        <a :href="route('studio.shop.item', { studioSlug: studio.slug, product: product.id })" title="Details">
                            <responsive-image type="product" :media="product.photo" :title="product.name" class="h-64"></responsive-image>
                        </a>
                    </div>
                    <div class="details mt-auto flex items-center justify-between space-x-2">
                        <p class="mt-0 flex-1">
                            <a
                                :href="route('studio.shop.item', { studioSlug: studio.slug, product: product.id })"
                                class="button button-xs button-green w-full rounded-none"
                                title="Details"
                            >
                                Details
                            </a>
                        </p>
                        <p class="mt-0 flex-1 text-center">
                            {{ priceFor(product) | asCurrency }}
                        </p>
                    </div>
                </div>
            </div>
            <pagination :meta="meta" class="mt-4" :hide-on-single-page="true" @size-change="handleSizeChange" @current-change="handleCurrentChange"></pagination>
        </div>
    </layout>
</template>

<script>
    import axios from '@/axios';
    import bus from '@/bus';
    import hasCart from '@/mixins/hasCart';
    import asCurrency from '@/filters/asCurrency';
    import Layout from '@/frontend/components/Studio/Shop/Layout.vue';
    import Pagination from '@/components/Pagination.vue';
    import ResponsiveImage from '@/components/ResponsiveImage.vue';

    export default {
        name: 'Browse',
        components: {
            Layout,
            Pagination,
            ResponsiveImage,
        },
        filters: {
            asCurrency,
        },
        mixins: [hasCart],
        props: {
            studio: {
                required: true,
                type: Object,
            },
            entity: {
                required: true,
                type: Object,
            },
            selectedCategory: {
                type: Object,
                default: () => ({}),
            },
            selectedGroup: {
                type: Object,
                default: () => ({}),
            },
            search: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                data: [],
                meta: {
                    current_page: 1,
                    per_page: 12,
                    total: 12,
                },
                links: {},
                loaded: false,
                tableIsLoading: false,
                adding: false,
            };
        },
        created() {
            bus.$on('refresh-data', this.fetchData);

            this.fetchData();
        },
        beforeDestroy() {
            bus.$off('refresh-data');
        },
        methods: {
            priceFor(product) {
                if (product.settings.length > 0) {
                    const setting = product.settings.find((s) => s.studio_id === this.studio.id);

                    if (setting && setting.price !== null && setting.price.amount > 0) {
                        return setting.price;
                    }
                }

                return product.msrp;
            },
            inventoryFor(product) {
                const inv = product.inventory.find((inventory) => inventory.studio_id === this.studio.id);

                return inv ? inv.quantity : 0;
            },
            queryServer(customProperties) {
                this.tableIsLoading = true;
                this.loaded = false;

                const properties = {
                    page: this.meta.current_page,
                    per_page: this.meta.per_page,
                    studioSlug: this.studio.slug,
                    ...this.properties,
                    ...customProperties,
                };

                if (this.selectedCategory) {
                    properties.category = this.selectedCategory.id;
                }

                if (this.selectedGroup) {
                    properties.group = this.selectedGroup.id;
                }

                if (this.search) {
                    properties.search = this.search;
                }

                return axios.get(route('studio.shop.inventory', properties)).then((response) => {
                    this.tableIsLoading = false;
                    this.data = response.data.data;
                    this.meta = response.data.meta;
                    this.links = response.data.links;

                    window.dispatchEvent(new CustomEvent('data:loaded'));
                    this.loaded = true;
                });
            },
            fetchData() {
                this.queryServer();
            },
            handleSizeChange(event) {
                this.queryServer({ per_page: event });
            },
            handleCurrentChange(event) {
                this.queryServer({ page: event });
            },
        },
    };
</script>

<style>
    @media (max-width: 700px) {
        .el-pagination span:not([class*='suffix']) {
            display: block;
            margin: 0.5rem 0;
        }
    }
</style>
